import EmptyState from "../../../../../shared/components/EmptyState";
import SearchInput from "../../../../../shared/components/SearchInput";
import { siteTexts } from "../../../../../shared/i18n/siteTexts";
import { formatEventDateRangeLabel } from "../../../../../shared/utils/dateTime";
import type {
  ChallengeHistoryItem,
  ChallengeStatus,
  ChallengeType,
} from "../../../../challenges/types/challengeAdmin";
import ProfileEventLocation from "../../../shared/components/ProfileEventLocation";
import ProfileAccordionSection from "../ProfileAccordionSection";
import UserProfileSectionHeader from "../UserProfileSectionHeader";

export type EventStatusFilter = "all" | ChallengeStatus;
export type EventTypeFilter = "all" | ChallengeType;

export type ProfileEventStats = {
  events: number;
  matches: number;
  titles: number;
  runnersUp: number;
  category: string;
  rankingPosition: number | null;
};

type ProfileEventsTabProps = {
  open: boolean;
  onToggle: () => void;
  nameFilter: string;
  onNameFilterChange: (value: string) => void;
  statusFilter: EventStatusFilter;
  onStatusFilterChange: (value: EventStatusFilter) => void;
  typeFilter: EventTypeFilter;
  onTypeFilterChange: (value: EventTypeFilter) => void;
  stats: ProfileEventStats;
  loading: boolean;
  events: ChallengeHistoryItem[];
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onCopySuccess: (message: string) => void;
  onCopyError: (message: string) => void;
};

function eventStatusFilters(): Array<{
  value: EventStatusFilter;
  label: string;
}> {
  const labels = siteTexts.profile.user.dashboard.eventFilters;
  return [
    { value: "all", label: labels.all },
    { value: "pending", label: labels.pending },
    { value: "started", label: labels.started },
    { value: "finished", label: labels.finished },
  ];
}

function eventTypeFilters(): Array<{
  value: EventTypeFilter;
  label: string;
}> {
  const labels = siteTexts.profile.user.dashboard.eventFilters;
  return [
    { value: "tournament", label: labels.tournaments },
    { value: "challenge", label: labels.challenge },
    { value: "super8", label: labels.super8 },
    { value: "poco", label: labels.poco },
  ];
}

function profileEventTypeLabel(type?: string | null) {
  const normalized = String(type || "")
    .trim()
    .toLowerCase();
  if (normalized === "tournament")
    return siteTexts.challenges.modes.tournament.title;
  if (normalized === "super8") return siteTexts.challenges.modes.super8.title;
  if (normalized === "poco") return siteTexts.challenges.modes.poco.title;
  return siteTexts.challenges.modes.challenge.title;
}

function profileEventTypeIcon(type?: string | null) {
  const normalized = String(type || "")
    .trim()
    .toLowerCase();
  if (normalized === "tournament") return "emoji_events";
  if (normalized === "super8") return "workspace_premium";
  if (normalized === "poco") return "my_location";
  return "bolt";
}

function profileEventStatusLabel(status?: string | null) {
  const normalized = String(status || "")
    .trim()
    .toLowerCase();
  const statuses = siteTexts.challenges.publicDetail.statuses;
  if (normalized === "finished") return statuses.finished;
  if (normalized === "started") return statuses.started;
  if (normalized === "canceled") return statuses.canceled;
  return statuses.pending;
}

function profileEventStatusTone(status?: string | null) {
  const normalized = String(status || "")
    .trim()
    .toLowerCase();
  if (normalized === "finished") return "finished";
  if (normalized === "started") return "started";
  if (normalized === "canceled") return "canceled";
  return "pending";
}

export default function ProfileEventsTab({
  open,
  onToggle,
  nameFilter,
  onNameFilterChange,
  statusFilter,
  onStatusFilterChange,
  typeFilter,
  onTypeFilterChange,
  stats,
  loading,
  events,
  page,
  totalPages,
  onPageChange,
  onCopySuccess,
  onCopyError,
}: ProfileEventsTabProps) {
  const dashboardTexts = siteTexts.profile.user.dashboard;

  return (
    <ProfileAccordionSection
      id="profile-history"
      icon="history"
      title={dashboardTexts.eventsTitle}
      subtitle={dashboardTexts.eventsSubtitle}
      open={open}
      onToggle={onToggle}
    >
      <div className="user-profile-content-section user-events-card">
        <UserProfileSectionHeader
          className="user-events-header"
          icon="emoji_events"
          title={dashboardTexts.eventsTitle}
          description={dashboardTexts.eventsSubtitle}
          actions={
            <div className="user-events-search">
              <SearchInput
                inputType="search"
                value={nameFilter}
                onChange={(event) => onNameFilterChange(event.target.value)}
                onClear={() => onNameFilterChange("")}
                placeholder={dashboardTexts.searchEvent}
                aria-label={dashboardTexts.searchEvent}
              />
              <button type="button" aria-label={dashboardTexts.filtersAria}>
                <span className="material-symbols-outlined" aria-hidden="true">
                  tune
                </span>
              </button>
            </div>
          }
        />

        <div className="user-events-summary-grid">
          {[
            {
              icon: "emoji_events",
              value: stats.events,
              label: dashboardTexts.stats.events,
              tone: "pink",
            },
            {
              icon: "sports_tennis",
              value: stats.matches,
              label: dashboardTexts.stats.playedMatches,
              tone: "blue",
            },
            {
              icon: "military_tech",
              value: stats.titles,
              label: dashboardTexts.stats.titles,
              tone: "gold",
            },
            {
              icon: "workspace_premium",
              value: stats.runnersUp,
              label: dashboardTexts.stats.runnersUp,
              tone: "purple",
            },
          ].map((stat) => (
            <article
              key={stat.label}
              className={`user-events-stat user-events-stat--${stat.tone}`}
            >
              <span className="material-symbols-outlined" aria-hidden="true">
                {stat.icon}
              </span>
              <div>
                <strong>{stat.value}</strong>
                <small>{stat.label}</small>
              </div>
            </article>
          ))}
        </div>

        <div className="user-events-filter-row">
          <div
            className="user-events-filter-group"
            aria-label={dashboardTexts.eventStatusAria}
          >
            {eventStatusFilters().map((filter) => (
              <button
                key={`event-status-${filter.value}`}
                type="button"
                className={statusFilter === filter.value ? "is-active" : ""}
                onClick={() => onStatusFilterChange(filter.value)}
              >
                {filter.label}
              </button>
            ))}
          </div>
          <div
            className="user-events-filter-group"
            aria-label={dashboardTexts.eventTypeAria}
          >
            {eventTypeFilters().map((filter) => (
              <button
                key={`event-type-${filter.value}`}
                type="button"
                className={typeFilter === filter.value ? "is-active" : ""}
                onClick={() => onTypeFilterChange(filter.value)}
              >
                <span className="material-symbols-outlined" aria-hidden="true">
                  {profileEventTypeIcon(filter.value)}
                </span>
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        {loading && (
          <div className="user-events-skeleton-list" aria-hidden="true">
            {Array.from({ length: 3 }).map((_, index) => (
              <div
                key={`user-event-skeleton-${index}`}
                className="user-event-skeleton"
              />
            ))}
          </div>
        )}

        {!loading && events.length === 0 && (
          <EmptyState
            className="profile-empty-state"
            icon={<span className="material-symbols-outlined">event_busy</span>}
            title={dashboardTexts.eventEmpty}
          />
        )}

        {!loading && events.length > 0 && (
          <>
            <div className="user-events-list">
              {events.map((item) => (
                <article key={item.id} className="user-event-card">
                  {item.eventImageUrl ? (
                    <div className="user-event-cover">
                      <img
                        src={item.eventImageUrl}
                        alt={
                          item.challengeName ||
                          item.groupName ||
                          dashboardTexts.eventsTitle
                        }
                        loading="lazy"
                      />
                    </div>
                  ) : (
                    <div
                      className={`user-event-type-icon user-event-type-icon--${item.challengeType}`}
                    >
                      <span
                        className="material-symbols-outlined"
                        aria-hidden="true"
                      >
                        {profileEventTypeIcon(item.challengeType)}
                      </span>
                    </div>
                  )}
                  <div className="user-event-main">
                    <h3>{item.challengeName || item.groupName}</h3>
                    <span
                      className={`user-event-type-badge is-${item.challengeType}`}
                    >
                      {profileEventTypeLabel(item.challengeType)}
                    </span>
                    <div className="user-event-details">
                      <div className="user-event-meta">
                        <span
                          className="material-symbols-outlined"
                          aria-hidden="true"
                        >
                          calendar_today
                        </span>
                        <span>
                          {formatEventDateRangeLabel(
                            item.scheduledAt || item.createdAt,
                            item.scheduledEndAt,
                          )}
                        </span>
                      </div>
                      <ProfileEventLocation
                        item={item}
                        onCopySuccess={onCopySuccess}
                        onCopyError={onCopyError}
                      />
                      <div className="user-event-meta">
                        <span
                          className="material-symbols-outlined"
                          aria-hidden="true"
                        >
                          groups
                        </span>
                        <span>
                          {item.matchesCount}{" "}
                          {siteTexts.profile.user.challenges.matchesSuffix}
                        </span>
                      </div>
                    </div>
                    <div className="user-event-actions">
                      <span
                        className={`user-event-status is-${profileEventStatusTone(
                          item.status,
                        )}`}
                      >
                        {profileEventStatusLabel(item.status)}
                      </span>
                      <a
                        className="user-event-details-link"
                        href={`#/desafios/detalhe/${item.id}`}
                        aria-label={`Ver detalhes de ${
                          item.challengeName || item.groupName || "evento"
                        }`}
                        title="Ver detalhes"
                      >
                        <span
                          className="material-symbols-outlined"
                          aria-hidden="true"
                        >
                          chevron_right
                        </span>
                      </a>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            <div
              className="user-events-pagination"
              aria-label={siteTexts.profile.user.challenges.paginationAria}
            >
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => onPageChange(Math.max(1, page - 1))}
                aria-label={siteTexts.profile.user.challenges.previousPage}
              >
                <span className="material-symbols-outlined" aria-hidden="true">
                  chevron_left
                </span>
              </button>
              {Array.from({ length: totalPages }).map((_, index) => {
                const nextPage = index + 1;
                return (
                  <button
                    key={`events-page-${nextPage}`}
                    type="button"
                    className={page === nextPage ? "is-active" : ""}
                    onClick={() => onPageChange(nextPage)}
                  >
                    {nextPage}
                  </button>
                );
              })}
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => onPageChange(Math.min(totalPages, page + 1))}
                aria-label={siteTexts.challenges.publicList.paging.nextAria}
              >
                <span className="material-symbols-outlined" aria-hidden="true">
                  chevron_right
                </span>
              </button>
            </div>
          </>
        )}
      </div>
    </ProfileAccordionSection>
  );
}
